/**
 * El ciclo de vida de la consola de partido: SSE + snapshot + cola + red caída.
 *
 * ## LA REGLA: EL SSE ES EL PULSO, EL SNAPSHOT ES LA VERDAD
 *
 * Este hook **no parsea ni un evento** del stream. Lo usa como campana: llegó
 * algo, pedí el snapshot de vuelta. Suena redundante y no lo es:
 *
 *  1. La pantalla de estadio y la tablet de banco entran con credenciales que
 *     **no tienen `events:read`**. Derivar el marcador del stream obligaría a
 *     darles el log crudo del partido para pintar un número.
 *  2. Quien digiere es el MOTOR, del lado del server. Un espejo del supervisor
 *     que contara los tiempos distinto del acta no sería un bug de UI: sería
 *     otro documento.
 *  3. Un reductor en el cliente tendría que entender `undo`, `correction`,
 *     sustituciones excepcionales y reemplazos de líbero. Ya existe uno que lo
 *     hace bien y está testeado; no hacen falta dos.
 *
 * El costo es un GET por rally (con rebote de 300 ms, porque los eventos vienen
 * en ráfaga: toque, toque, punto). Con siete pantallas son siete pedidos cada
 * treinta segundos.
 *
 * ## LA COLA VA POR POLL, Y ESTÁ DICHO POR QUÉ
 *
 * El bus del vivo transporta EVENTOS y se recupera releyendo el log por `seq`.
 * Un intent no tiene `seq` y no está en el log, así que necesitaría un segundo
 * bus distribuido —puerto, dos implementaciones, test de contrato— para mover
 * un pedido que un humano contesta en segundos. Se pollea con cursor: la
 * respuesta normal es cero filas contra un índice.
 *
 * ## Y CUANDO SE CAE LA RED (§5.3)
 *
 * El partido sigue. Este hook nunca borra lo que ya mostró: si el snapshot
 * falla, se conserva el anterior y sube `linkState`. El último estado conocido
 * queda además en `localStorage`, así que un F5 o una tablet que se durmió
 * vuelven con el marcador puesto en vez de con una pantalla en blanco.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MatchReport } from '@scout/core';
import type { ApiClient } from '../../lib/api-client';
import {
  loadBenchCache,
  mergeIntents,
  saveBenchCache,
  type BenchIntent,
  type BenchSnapshot,
  type LinkState,
} from '../../lib/bench';
import { linkState } from '../../lib/bench';
import { LiveSubscription, type LiveStatus } from '../../lib/live';

/** Rebote del refresco: los eventos llegan en ráfaga (toque, toque, punto). */
const REFRESH_DEBOUNCE_MS = 300;
/** Poll de la cola. Un pedido lo atiende una persona: 2 s es invisible. */
const QUEUE_POLL_MS = 2_000;
/** Reloj de pantalla: la antigüedad del dato y la del pedido se muestran. */
const TICK_MS = 1_000;

export interface BenchConsole {
  snapshot: BenchSnapshot | null;
  intents: BenchIntent[];
  status: LiveStatus;
  link: LinkState;
  /** Reloj del navegador, refrescado cada segundo (para edades y cuentas atrás). */
  now: number;
  /** Cuándo se recibió el último snapshot bueno (reloj del navegador). */
  lastSyncAt: number | null;
  /** El snapshot que se está mostrando salió del caché, no de la red. */
  fromCache: boolean;
  /** Último error de red/permiso, para el cartel. Nunca borra lo que ya se ve. */
  error: string | null;
  /** Pide todo de nuevo (lo llama el botón de reintentar y cada acción). */
  refresh: () => void;
  /** Aplica en el acto un intent que devolvió una acción, sin esperar al poll. */
  applyIntent: (intent: BenchIntent) => void;
}

export function useBenchConsole(client: ApiClient, matchId: string): BenchConsole {
  const [snapshot, setSnapshot] = useState<BenchSnapshot | null>(null);
  const [intents, setIntents] = useState<BenchIntent[]>([]);
  const [status, setStatus] = useState<LiveStatus>('idle');
  const [lastSyncAt, setLastSyncAt] = useState<number | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  /** Se incrementa con cada pulso del SSE y con cada acción: dispara el refresco. */
  const [beat, setBeat] = useState(0);

  const cursor = useRef<number | null>(null);
  const alive = useRef(true);

  const refresh = useCallback(() => setBeat((n) => n + 1), []);

  const applyIntent = useCallback((intent: BenchIntent) => {
    setIntents((prev) => mergeIntents(prev, [intent]));
  }, []);

  // --- Caché: lo primero que se dibuja, antes de que la red conteste ---------
  useEffect(() => {
    alive.current = true;
    const cached = loadBenchCache(matchId);
    if (cached) {
      setSnapshot(cached.snapshot);
      setIntents(cached.snapshot.intents ?? []);
      setFromCache(true);
      // OJO: `lastSyncAt` queda con la hora en que se GUARDÓ, no "ahora". Si se
      // pusiera ahora, una tablet que se abre sin red mostraría un marcador de
      // hace media hora con el cartelito verde de "en vivo".
      setLastSyncAt(cached.savedAt);
    }
    return () => {
      alive.current = false;
    };
  }, [matchId]);

  // --- Reloj de pantalla -----------------------------------------------------
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  // --- El pulso: SSE ---------------------------------------------------------
  useEffect(() => {
    const subscription = new LiveSubscription({
      url: client.liveUrl(matchId),
      headers: client.authHeaders(),
      onStatus: (next) => {
        if (alive.current) setStatus(next);
      },
      // Al conectar y en cada reconexión: pedir todo de nuevo cubre el hueco
      // sin tener que razonar sobre qué eventos se perdieron.
      onSnapshot: () => refresh(),
      onEvent: () => refresh(),
    });
    subscription.start();
    return () => subscription.stop();
  }, [client, matchId, refresh]);

  // --- La verdad: el snapshot ------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      client
        .getBenchSnapshot(matchId)
        .then((next) => {
          if (cancelled || !alive.current) return;
          setSnapshot(next);
          setIntents((prev) => mergeIntents(prev, next.intents ?? []));
          cursor.current = next.intentVersion ?? 0;
          setLastSyncAt(Date.now());
          setFromCache(false);
          setError(null);
          saveBenchCache(matchId, next);
        })
        .catch((err: unknown) => {
          if (cancelled || !alive.current) return;
          // NO se borra el snapshot: la promesa de §5.3 es que el partido
          // sigue en pantalla. Lo que cambia es el cartel.
          setError(err instanceof Error ? err.message : String(err));
        });
    }, REFRESH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [client, matchId, beat]);

  // --- La cola: poll con cursor ---------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const tick = (): void => {
      const since = cursor.current;
      if (since === null) return;
      client
        .listIntents(matchId, since)
        .then((page) => {
          if (cancelled || !alive.current) return;
          cursor.current = page.intentVersion;
          if (page.intents.length > 0) setIntents((prev) => mergeIntents(prev, page.intents));
        })
        .catch(() => {
          // Un poll fallido no dice nada nuevo: el estado del enlace ya lo
          // cuenta el SSE. Ensuciar el cartel con esto sólo agrega ruido.
        });
    };
    const id = setInterval(tick, QUEUE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [client, matchId]);

  const link = useMemo(() => linkState(status, lastSyncAt, now), [status, lastSyncAt, now]);

  return {
    snapshot,
    intents,
    status,
    link,
    now,
    lastSyncAt,
    fromCache,
    error,
    refresh,
    applyIntent,
  };
}

/** Cada cuánto se refrescan los números del partido. */
const NUMBERS_POLL_MS = 45_000;

export interface MatchNumbers {
  report: MatchReport | null;
  /** El rol no puede pedir el reporte (403) o el package todavía no está (501). */
  unavailable: boolean;
}

/**
 * Los reportes clave del partido EN CURSO.
 *
 * Van por `GET /report` y no por el snapshot a propósito: el snapshot es lo que
 * se refresca en cada rally y tiene que ser chico, y esto no cambia de un punto
 * al otro de manera que el DT pueda leer a un metro. Cuarenta y cinco segundos
 * es más frecuente de lo que alguien mira un porcentaje.
 */
export function useMatchNumbers(client: ApiClient, matchId: string): MatchNumbers {
  const [report, setReport] = useState<MatchReport | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  /** Si alguna vez llegó, un fallo posterior NO borra lo que ya se está viendo. */
  const everLoaded = useRef(false);

  useEffect(() => {
    let cancelled = false;
    everLoaded.current = false;
    const load = (): void => {
      client
        .getMatchReport(matchId)
        .then((next) => {
          if (cancelled) return;
          everLoaded.current = true;
          setReport(next);
          setUnavailable(false);
        })
        .catch(() => {
          // Sin números se sigue mostrando el partido: son un accesorio de la
          // consola, no la consola. Lo que NO se hace es borrar los anteriores.
          if (!cancelled && !everLoaded.current) setUnavailable(true);
        });
    };
    load();
    const id = setInterval(load, NUMBERS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [client, matchId]);

  return { report, unavailable };
}

